import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ScannerPage from "./pages/ScannerPage";
import LocaisPage from "./pages/LocaisPage";
import EmpresasPage from "./pages/EmpresasPage";
import ViaturasPage from "./pages/ViaturasPage";
import UsersPage from "./pages/UsersPage";
import MovimentosPage from "./pages/MovimentosPage";
import IncidenciasPage from "./pages/IncidenciasPage";
import QuotasPage from "./pages/QuotasPage";
import ContactosPage from "./pages/ContactosPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("gape_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Token verification failed:", error);
          logout();
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem("gape_token", access_token);
      setToken(access_token);
      setUser(userData);
      toast.success("Login efetuado com sucesso");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao efetuar login";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (nome, email, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { nome, email, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem("gape_token", access_token);
      setToken(access_token);
      setUser(userData);
      toast.success("Registo efetuado com sucesso");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao efetuar registo";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem("gape_token");
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === "super_admin",
    isGestorLocal: user?.role === "gestor_local",
    isGestorEmpresa: user?.role === "gestor_empresa",
    isOperador: user?.role === "operador_parque"
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("gape_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("gape_token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          
          <Route path="/scanner" element={
            <ProtectedRoute allowedRoles={["super_admin", "gestor_local", "operador_parque"]}>
              <ScannerPage />
            </ProtectedRoute>
          } />
          
          <Route path="/locais" element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <LocaisPage />
            </ProtectedRoute>
          } />
          
          <Route path="/empresas" element={
            <ProtectedRoute allowedRoles={["super_admin", "gestor_local"]}>
              <EmpresasPage />
            </ProtectedRoute>
          } />
          
          <Route path="/quotas" element={
            <ProtectedRoute allowedRoles={["super_admin", "gestor_local"]}>
              <QuotasPage />
            </ProtectedRoute>
          } />
          
          <Route path="/viaturas" element={
            <ProtectedRoute allowedRoles={["super_admin", "gestor_local", "gestor_empresa"]}>
              <ViaturasPage />
            </ProtectedRoute>
          } />
          
          <Route path="/users" element={
            <ProtectedRoute allowedRoles={["super_admin", "gestor_local", "gestor_empresa"]}>
              <UsersPage />
            </ProtectedRoute>
          } />
          
          <Route path="/movimentos" element={
            <ProtectedRoute>
              <MovimentosPage />
            </ProtectedRoute>
          } />
          
          <Route path="/incidencias" element={
            <ProtectedRoute>
              <IncidenciasPage />
            </ProtectedRoute>
          } />
          
          <Route path="/contactos" element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <ContactosPage />
            </ProtectedRoute>
          } />
          
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
